import React, {
  createContext, useContext, useState, useEffect,
} from 'react';
import Cookies from 'universal-cookie';

import { isMediaType, resolveFavoriteToggle } from './FavoriteHelpers';
import FavoriteSyncError from './FavoriteSyncError';

const cookies = new Cookies();

const FavoriteButtonsContext = createContext();

export const useFavoriteButtons = () => {
  const context = useContext(FavoriteButtonsContext);
  if (!context) {
    throw new Error('useFavoriteButtons must be used within a FavoriteButtonsProvider');
  }
  return context;
};

/**
 * Reads the residence -> floor-plan provenance map out of its cookie.
 *
 * Favoriting a residence also favorites its floor plan, so un-favoriting it has
 * to un-favorite the floor plan again — but only the floor plans it put there.
 * This map records which residence auto-added which floor plan, so a floor plan
 * the counselor favorited on its own, or one still claimed by another favorited
 * residence, survives.
 *
 * @returns {Object} { [residenceUuid]: floorPlanUuid }
 */
const getAutoFloorPlans = () => cookies.get('residence_favorites')?.auto_floor_plans || {};

export const FavoriteButtonsProvider = ({ children }) => {
  const [favorites, setFavorites] = useState({});
  const [mediaFavorites, setMediaFavorites] = useState({});
  const [residenceFavorites, setResidenceFavorites] = useState({});
  // Whether the last favorite PATCH to the resident failed. The provider wraps
  // every place a heart renders, so the message has one home rather than one
  // per button.
  const [syncFailed, setSyncFailed] = useState(false);

  // Track the cookie *contents*, not just its existence, so starting a new
  // brochure (which overwrites the cookie) or ending personalization (which
  // removes it) re-syncs in-memory state without needing a full page reload.
  const favoritesCookie = cookies.get('favorites');
  const favoritesCookieString = JSON.stringify(favoritesCookie?.favorites ?? {});
  useEffect(() => {
    setFavorites(favoritesCookie?.favorites ?? {});
  }, [favoritesCookieString]);

  const mediaFavoritesCookie = cookies.get('media_favorites');
  const mediaFavoritesCookieString = JSON.stringify(mediaFavoritesCookie?.favorites ?? {});
  useEffect(() => {
    setMediaFavorites(mediaFavoritesCookie?.favorites ?? {});
  }, [mediaFavoritesCookieString]);

  const residenceFavoritesCookie = cookies.get('residence_favorites');
  const residenceFavoritesCookieString = JSON.stringify(residenceFavoritesCookie?.favorites ?? {});
  useEffect(() => {
    setResidenceFavorites(residenceFavoritesCookie?.favorites ?? {});
  }, [residenceFavoritesCookieString]);

  // Imperative re-sync for callers that change the favorites cookies directly:
  // starting a new brochure seeds them, and Stop/Cancel personalization removes
  // them. Calling this right after those writes updates in-memory state
  // immediately, without relying on the provider happening to re-render.
  const syncFavoritesFromCookies = () => {
    setFavorites(cookies.get('favorites')?.favorites ?? {});
    setMediaFavorites(cookies.get('media_favorites')?.favorites ?? {});
    setResidenceFavorites(cookies.get('residence_favorites')?.favorites ?? {});
  };

  const isFavorited = (type, uuid) => {
    if (isMediaType(type)) {
      return mediaFavorites[type]?.includes(uuid) || false;
    }
    if (type === 'residence') {
      return residenceFavorites[type]?.includes(uuid) || false;
    }
    return favorites[type]?.includes(uuid) || false;
  };

  const writeCookie = (cookieName, data) => {
    cookies.remove(cookieName);
    cookies.set(cookieName, data, {
      path: '/',
      maxAge: 14400, // 4 hour cookie expiration
      domain: window.location.hostname,
    });
  };

  const toggleFavorite = (type, uuid, floorPlanUuid = null) => {
    const result = resolveFavoriteToggle(
      {
        favorites,
        mediaFavorites,
        residenceFavorites,
        autoFloorPlans: getAutoFloorPlans(),
      },
      type,
      uuid,
      floorPlanUuid,
    );

    const setters = {
      favorites: setFavorites,
      mediaFavorites: setMediaFavorites,
      residenceFavorites: setResidenceFavorites,
    };
    const cookieNames = {
      favorites: 'favorites',
      mediaFavorites: 'media_favorites',
      residenceFavorites: 'residence_favorites',
    };

    setters[result.bucket](result.toggled);

    // Every write carries the provenance map along with the residence
    // favorites, because that's the cookie that holds it — even when the toggle
    // that changed the map was a floor plan's.
    const residences = result.bucket === 'residenceFavorites'
      ? result.toggled
      : cookies.get('residence_favorites')?.favorites || residenceFavorites;

    writeCookie(cookieNames[result.bucket], result.bucket === 'residenceFavorites'
      ? { favorites: result.toggled, auto_floor_plans: result.autoFloorPlans }
      : { favorites: result.toggled });

    if (result.bucket !== 'residenceFavorites') {
      writeCookie('residence_favorites', {
        favorites: residences,
        auto_floor_plans: result.autoFloorPlans,
      });
    }

    // A residence toggle that also moved its floor plan.
    if (result.floorPlans) {
      setFavorites(result.floorPlans);
      writeCookie('favorites', { favorites: result.floorPlans });
    }

    return result.nowFavorited;
  };

  const value = {
    favorites,
    setFavorites,
    mediaFavorites,
    residenceFavorites,
    setResidenceFavorites,
    isFavorited,
    toggleFavorite,
    syncFavoritesFromCookies,
    syncFailed,
    setSyncFailed,
  };

  return (
    <FavoriteButtonsContext.Provider value={value}>
      {children}
      <FavoriteSyncError visible={syncFailed} onDismiss={() => setSyncFailed(false)} />
    </FavoriteButtonsContext.Provider>
  );
};

export default FavoriteButtonsContext;
